import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import {
  BiBriefcase,
  BiPieChart,
  BiClipboard,
  BiGroup,
  BiBuilding,
  BiGrid,
  BiChevronRight,
  BiBook,
  BiFolder
} from 'react-icons/bi';
import { platformAPI } from '../../services/platformAPI';
import { TENDER_APP_NAME } from '../../config/branding';
import { formatRecentWhen, getWorkspaceRecent } from '../../utils/workspaceRecent';
import './WorkspaceHome.scss';

const APP_ICONS = {
  tender: BiClipboard,
  project_delivery: BiBriefcase,
  finance_visibility: BiPieChart,
  people_policies: BiGroup,
  crm_vendor: BiBuilding
};

const HOME_CARD_KEYS = ['tender', 'project_delivery', 'finance_visibility'];
const ALSO_PLANNED_KEYS = ['people_policies', 'crm_vendor'];

const HOME_BLURBS = {
  tender: 'Assess opportunities, prepare bids and manage approvals.',
  project_delivery: 'Milestones, quality and assignment visibility.',
  finance_visibility: 'Budgets, cash flow and management reporting.'
};

const RECENT_ICONS = {
  '/apps/tender/register': BiFolder,
  '/apps/tender/knowledge': BiBook
};

function timeGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

function displayName(app) {
  return app.key === 'tender' ? TENDER_APP_NAME : app.displayName;
}

function formatDueLabel(item) {
  if (!item.dueDate) return null;
  const due = new Date(item.dueDate);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dueDay = new Date(due.getFullYear(), due.getMonth(), due.getDate());
  const diff = Math.round((dueDay - today) / (24 * 60 * 60 * 1000));
  if (item.status === 'OVERDUE' || diff < 0) return { text: 'Overdue', tone: 'risk' };
  if (diff === 0) return { text: 'Due today', tone: 'warn' };
  if (diff === 1) return { text: 'Due tomorrow', tone: 'muted' };
  return {
    text: due.toLocaleDateString(undefined, { day: 'numeric', month: 'short' }),
    tone: 'muted'
  };
}

function workItemMeta(item) {
  if (item.description) return item.description;
  if (item.tenderId) return `${TENDER_APP_NAME} · ${String(item.tenderId).slice(-6)}`;
  return TENDER_APP_NAME;
}

const WorkspaceHome = () => {
  const navigate = useNavigate();
  const user = useSelector((s) => s.auth.user);
  const [loading, setLoading] = useState(true);
  const [apps, setApps] = useState([]);
  const [workItems, setWorkItems] = useState([]);

  const firstName = user?.name?.split(/\s+/)[0] || user?.email?.split('@')[0] || 'there';

  useEffect(() => {
    Promise.all([
      platformAPI.listApplications(),
      platformAPI.myWorkItems().catch(() => ({ data: { data: { items: [] } } }))
    ])
      .then(([appsRes, workRes]) => {
        setApps(appsRes.data?.data?.applications || []);
        const items = workRes.data?.data?.items || [];
        setWorkItems(
          items.filter((i) => !['COMPLETED', 'CANCELLED'].includes(i.status)).slice(0, 4)
        );
      })
      .finally(() => setLoading(false));
  }, []);

  const sortedApps = useMemo(
    () => [...apps].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)),
    [apps]
  );

  const featuredApps = useMemo(() => {
    const byKey = new Map(sortedApps.map((a) => [a.key, a]));
    return HOME_CARD_KEYS.map((key) => byKey.get(key)).filter(Boolean);
  }, [sortedApps]);

  const alsoPlanned = useMemo(() => {
    const byKey = new Map(sortedApps.map((a) => [a.key, a]));
    return ALSO_PLANNED_KEYS.map((key) => byKey.get(key)).filter(Boolean);
  }, [sortedApps]);

  const recent = useMemo(() => {
    const stored = getWorkspaceRecent(user?._id || user?.id);
    if (stored.length >= 2) return stored.slice(0, 4);
    return [
      { path: '/apps/tender/register', label: 'Tender register', visitedAt: Date.now() - 3600000 },
      { path: '/apps/tender/knowledge', label: 'Knowledge bank — reusable content', visitedAt: Date.now() - 86400000 },
      ...stored
    ].slice(0, 4);
  }, [user]);

  const now = new Date();
  const weekdayLabel = now.toLocaleDateString(undefined, { weekday: 'long' });
  const dateLabel = now.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });

  if (loading) {
    return (
      <div className="workspace-home workspace-home--loading">
        <Spinner animation="border" size="sm" />
      </div>
    );
  }

  return (
    <div className="workspace-home">
      <header className="workspace-home__welcome">
        <div>
          <h1 className="workspace-home__greeting">{timeGreeting()}, {firstName}</h1>
          <p className="workspace-home__lead">Choose an application to get started.</p>
        </div>
        <time className="workspace-home__date" dateTime={now.toISOString().slice(0, 10)}>
          <span className="workspace-home__date-day">{weekdayLabel}</span>
          <span className="workspace-home__date-full">{dateLabel}</span>
        </time>
      </header>

      <section className="workspace-home__section" aria-labelledby="ws-apps-title">
        <div className="workspace-home__section-head">
          <h2 id="ws-apps-title" className="workspace-home__section-title">Your applications</h2>
          <p className="workspace-home__section-sub">
            One workspace for your authorised business applications.
          </p>
        </div>

        <div className="workspace-home__app-grid">
          {featuredApps.map((app) => {
            const Icon = APP_ICONS[app.key] || BiGrid;
            const isPlanned = app.status === 'planned';
            const canOpen = app.key === 'tender' && app.orgEnabled && app.userCanAccess;
            const statusLabel = isPlanned ? 'Planned' : 'Available';
            const statusTone = isPlanned ? 'planned' : 'available';

            return (
              <article
                key={app.key}
                className={`workspace-home__app-card ${isPlanned ? 'workspace-home__app-card--planned' : 'workspace-home__app-card--active'}`}
              >
                <div className="workspace-home__app-card-top">
                  <span className={`workspace-home__status workspace-home__status--${statusTone}`}>
                    {statusLabel}
                  </span>
                </div>
                <div className={`workspace-home__app-icon workspace-home__app-icon--${app.key}`}>
                  <Icon size={22} aria-hidden />
                </div>
                <h3 className="workspace-home__app-name">{displayName(app)}</h3>
                <p className="workspace-home__app-desc">
                  {HOME_BLURBS[app.key] || app.description}
                </p>
                {canOpen ? (
                  <button
                    type="button"
                    className="workspace-home__app-open"
                    onClick={() => navigate('/apps/tender')}
                  >
                    Open application
                    <BiChevronRight size={18} aria-hidden />
                  </button>
                ) : (
                  <p className="workspace-home__app-soon">Coming soon</p>
                )}
              </article>
            );
          })}
        </div>

        {alsoPlanned.length ? (
          <div className="workspace-home__roadmap">
            <h3 className="workspace-home__roadmap-label">Coming next</h3>
            <div className="workspace-home__roadmap-grid">
              {alsoPlanned.map((app) => {
                const Icon = APP_ICONS[app.key] || BiGrid;
                return (
                  <div key={app.key} className="workspace-home__roadmap-card">
                    <span className={`workspace-home__app-icon workspace-home__app-icon--${app.key}`}>
                      <Icon size={18} aria-hidden />
                    </span>
                    <span className="workspace-home__roadmap-copy">
                      <span className="workspace-home__roadmap-name">{app.displayName}</span>
                      <span className="workspace-home__roadmap-state">Planned</span>
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}
      </section>

      <div className="workspace-home__columns">
        <section className="workspace-home__panel" aria-labelledby="ws-my-work-title">
          <div className="workspace-home__panel-head">
            <div>
              <h2 id="ws-my-work-title" className="workspace-home__panel-title">My work</h2>
              <p className="workspace-home__panel-sub">
                Tasks assigned to you across your applications
              </p>
            </div>
            <button type="button" className="workspace-home__panel-link" onClick={() => navigate('/my-work')}>
              View all
              <BiChevronRight size={18} aria-hidden />
            </button>
          </div>
          <ul className="workspace-home__list">
            {workItems.length ? (
              workItems.map((item) => {
                const due = formatDueLabel(item);
                return (
                  <li key={item._id}>
                    <button
                      type="button"
                      className="workspace-home__list-row"
                      onClick={() => item.routePath && navigate(item.routePath)}
                      disabled={!item.routePath}
                    >
                      <span className="workspace-home__list-icon workspace-home__list-icon--work" aria-hidden>
                        <BiClipboard size={18} />
                      </span>
                      <span className="workspace-home__list-body">
                        <span className="workspace-home__list-title">{item.title}</span>
                        <span className="workspace-home__list-meta">{workItemMeta(item)}</span>
                      </span>
                      {due ? (
                        <span className={`workspace-home__due workspace-home__due--${due.tone}`}>
                          {due.text}
                        </span>
                      ) : null}
                      <BiChevronRight className="workspace-home__list-chevron" aria-hidden />
                    </button>
                  </li>
                );
              })
            ) : (
              <li className="workspace-home__empty">No open tasks. You are clear to focus on new work.</li>
            )}
          </ul>
        </section>

        <section className="workspace-home__panel" aria-labelledby="ws-recent-title">
          <div className="workspace-home__panel-head">
            <h2 id="ws-recent-title" className="workspace-home__panel-title">Recently opened</h2>
          </div>
          <ul className="workspace-home__list">
            {recent.map((row) => {
              const Icon = RECENT_ICONS[row.path] || BiFolder;
              return (
                <li key={row.path}>
                  <button
                    type="button"
                    className="workspace-home__list-row"
                    onClick={() => navigate(row.path)}
                  >
                    <span className="workspace-home__list-icon workspace-home__list-icon--recent" aria-hidden>
                      <Icon size={18} />
                    </span>
                    <span className="workspace-home__list-body">
                      <span className="workspace-home__list-title">{row.label}</span>
                      <span className="workspace-home__list-meta">{formatRecentWhen(row.visitedAt)}</span>
                    </span>
                    <BiChevronRight className="workspace-home__list-chevron" aria-hidden />
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      </div>

      <footer className="workspace-home__footer">
        <span>Illustrative workspace preview</span>
        <span>Powered by Celeris Venture Systems</span>
      </footer>
    </div>
  );
};

export default WorkspaceHome;
